"use client";

import { type RefObject, useCallback, useEffect } from "react";

export interface PathFocusItem {
	pathIndex: number;
	stopId?: string;
}

interface UseHexagonMapPathKeyboardOptions {
	containerRef: RefObject<HTMLElement | null>;
	pathFocusOrder: PathFocusItem[];
	focusedIndex: number;
	onFocusedIndexChange: (index: number) => void;
	unlockedStopIds: Set<string>;
	onPathStopPress?: (stopId: string) => void;
	onKeyboardDismiss?: () => void;
	/** When false, map path keyboard navigation is inactive (e.g. scoped preview overlay). */
	enabled?: boolean;
}

export function useHexagonMapPathKeyboard(
	options: UseHexagonMapPathKeyboardOptions,
) {
	const {
		containerRef,
		pathFocusOrder,
		focusedIndex,
		onFocusedIndexChange,
		unlockedStopIds,
		onPathStopPress,
		onKeyboardDismiss,
		enabled = true,
	} = options;

	const moveFocus = useCallback(
		(delta: number) => {
			if (pathFocusOrder.length === 0) {
				return;
			}
			if (focusedIndex < 0) {
				onFocusedIndexChange(
					delta >= 0 ? 0 : pathFocusOrder.length - 1,
				);
				return;
			}
			const next =
				(focusedIndex + delta + pathFocusOrder.length) %
				pathFocusOrder.length;
			onFocusedIndexChange(next);
		},
		[focusedIndex, onFocusedIndexChange, pathFocusOrder.length],
	);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const el = containerRef.current;
		if (!el) {
			return;
		}

		function onKeyDown(event: KeyboardEvent) {
			if (pathFocusOrder.length === 0) {
				return;
			}

			switch (event.key) {
				case "ArrowUp":
				case "ArrowLeft":
					event.preventDefault();
					moveFocus(-1);
					break;
				case "ArrowDown":
				case "ArrowRight":
					event.preventDefault();
					moveFocus(1);
					break;
				case "Home":
					event.preventDefault();
					onFocusedIndexChange(0);
					break;
				case "End":
					event.preventDefault();
					onFocusedIndexChange(pathFocusOrder.length - 1);
					break;
				case "Enter":
				case " ": {
					if (focusedIndex < 0) {
						break;
					}
					const item = pathFocusOrder[focusedIndex];
					if (!item?.stopId) {
						break;
					}
					if (!unlockedStopIds.has(item.stopId)) {
						break;
					}
					event.preventDefault();
					onPathStopPress?.(item.stopId);
					break;
				}
				case "Escape":
					event.preventDefault();
					onKeyboardDismiss?.();
					break;
				default:
					break;
			}
		}

		el.addEventListener("keydown", onKeyDown);
		return () => el.removeEventListener("keydown", onKeyDown);
	}, [
		containerRef,
		enabled,
		focusedIndex,
		moveFocus,
		onFocusedIndexChange,
		onKeyboardDismiss,
		onPathStopPress,
		pathFocusOrder,
		unlockedStopIds,
	]);

	return { focusedItem: pathFocusOrder[focusedIndex] };
}
